import { BitkitControlButton, BitkitTable, BitkitTooltip, IconOpenInNew } from '@bitrise/bitkit-v2';
import { Collapsible } from '@chakra-ui/react/collapsible';
import { chakra } from '@chakra-ui/react/styled-system';
import { Text } from '@chakra-ui/react/text';

import htmlReportDisplayed from '@/analytics/htmlReportDisplayed';
import { HtmlReport, HtmlReportTrackingParams } from '@/models/HtmlReport';

/** Expand toggle, name, open-in-new-tab — the span the report preview has to cover. */
const COLUMN_COUNT = 3;

type Props = {
  htmlReports: HtmlReport[];
  isTableCaptionVisible?: boolean;
  trackingParams: Pick<HtmlReportTrackingParams, 'appSlug' | 'pipelineSlug' | 'tabName'>;
};

type HtmlReportsTableRowProps = {
  report: HtmlReport;
  trackingParams: Pick<HtmlReportTrackingParams, 'appSlug' | 'pipelineSlug' | 'tabName'>;
};
const HtmlArtifactReportsTableRow = ({
  report: {
    url,
    buildSlug,
    title: htmlReportTitle,
    category: htmlReportCategory,
    isAvailable: isHtmlReportAvailable,
    isExpired,
  },
  trackingParams,
}: HtmlReportsTableRowProps) => {
  if (!isHtmlReportAvailable) {
    const unavailableMessage = isExpired
      ? 'This report expired and is no longer available.'
      : 'This report is not available yet.';

    return (
      <BitkitTable.Row>
        {/* Pads the expand-toggle column BitkitTable.ExpandableRow renders for available rows. */}
        <BitkitTable.Cell />

        <BitkitTable.Cell color="text/tertiary">
          <BitkitTooltip text={unavailableMessage}>
            <Text as="span">{htmlReportTitle}</Text>
          </BitkitTooltip>
        </BitkitTable.Cell>

        {/* BitkitTable.RowActionsCell's action shape has no tooltip, and a disabled button
            dispatches no pointer events — hence the plain cell and the focusable wrapper. */}
        <BitkitTable.Cell textAlign="right">
          <BitkitTooltip text={unavailableMessage} wrapTrigger>
            <BitkitControlButton icon={IconOpenInNew} label="Open in new tab" state="disabled" />
          </BitkitTooltip>
        </BitkitTable.Cell>
      </BitkitTable.Row>
    );
  }

  return (
    <BitkitTable.ExpandableRow
      colSpan={COLUMN_COUNT}
      expandedContent={
        // BitkitTable.ExpandableRow leaves the collapsed content mounted, and an iframe inside a `hidden`
        // element still loads and still fires `onLoad` — so every report would count as displayed on
        // page render. Gate the iframe on the row's own open state instead.
        <Collapsible.Context>
          {({ visible }) =>
            visible && (
              <chakra.iframe
                height="50vh"
                onLoad={() => {
                  htmlReportDisplayed({
                    ...trackingParams,
                    buildSlug,
                    htmlReportCategory,
                    htmlReportTitle,
                    isHtmlReportAvailable,
                  });
                }}
                sandbox="allow-scripts"
                src={url}
                title={htmlReportTitle}
                width="100%"
              />
            )
          }
        </Collapsible.Context>
      }
    >
      <BitkitTable.Cell>{htmlReportTitle}</BitkitTable.Cell>
      <BitkitTable.RowActionsCell
        actions={[{ href: url, icon: IconOpenInNew, isExternal: true, label: 'Open in new tab' }]}
      />
    </BitkitTable.ExpandableRow>
  );
};

const HtmlReportsTable = ({ htmlReports, isTableCaptionVisible, trackingParams }: Props) => {
  if (!htmlReports.length) {
    return null;
  }

  return (
    <BitkitTable.ScrollArea>
      <BitkitTable.Root>
        {isTableCaptionVisible && (
          <BitkitTable.Caption color="text/primary" marginBlockEnd="12" textStyle="heading/h3">
            HTML Reports
          </BitkitTable.Caption>
        )}

        <BitkitTable.Header>
          <BitkitTable.Row>
            <BitkitTable.ColumnHeader />
            <BitkitTable.ColumnHeader>Name</BitkitTable.ColumnHeader>
            <BitkitTable.ColumnHeader />
          </BitkitTable.Row>
        </BitkitTable.Header>

        <BitkitTable.Body>
          {htmlReports.map((report) => (
            <HtmlArtifactReportsTableRow key={report.url} report={report} trackingParams={{ ...trackingParams }} />
          ))}
        </BitkitTable.Body>
      </BitkitTable.Root>
    </BitkitTable.ScrollArea>
  );
};

export default HtmlReportsTable;
